import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useMountEffect from '../../hooks/useMountEffect';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	ALL_COMPARISON_CHART_KEYS,
	CHART_METRIC,
	COMPARISON_CHART_KEYS,
	VIEW_MODE,
} from './LifeCycleAnalyticsConstants';
import {
	chartComparisonsToQueryParams,
	clampDateRange,
	filtersToParams,
	filtersToQueryParams,
	filtersWithCompletedAtRange,
	getDefaultChartComparisons,
	getDefaultFilters,
	parseSafeInt,
	queryParamsToChartComparisons,
	queryParamsToFilters,
} from './LifeCycleAnalyticsUtils';
import {
	COMPARISON_MODE,
	ComparisonState,
	resolveComparisonRange,
} from '../../utils/AnalyticsComparisonUtils';
import queryString from 'qs';
import Color from 'color';

const BACK_LINK_TEXT = 'Back to Life Cycle Analytics';

/**
 * URL sync, server state, priority/metric navigation, and filter draft handlers
 * for Lifecycle Analytics overview.
 */
export function useLifeCycleAnalyticsData(props: any) {
	const {
		companyConfig,
		currentUser,
		userType,
		problemTypes,
		workOrderPriorities,
		fetchPrioritySummary,
		fetchSupplierPerformance,
		fetchMonthlyApprovalTime,
		fetchMonthlyResponseTime,
		fetchMonthlyResolutionTime,
		fetchMonthlyFirstTimeFixRate,
		fetchMonthlySpend,
		fetchProblemTypes,
		fetchWorkOrderPriorities,
		updateNavbar,
		clearNavbar,
	} = props;

	const history = useHistory();
	const location = useLocation();

	const { initialFilters, initialPriorityId, initialMinWoCount, initialChartComparisons } =
		useMemo(() => {
			const search = location.search;
			const str = search && search[0] === '?' ? search.slice(1) : search;
			const qp = queryString.parse(str || '');
			const hasParams = Object.keys(qp).length > 0;
			return {
				initialFilters: hasParams ? queryParamsToFilters(qp) : getDefaultFilters(),
				initialPriorityId: qp.priorityId ? parseSafeInt(qp.priorityId, null) : null,
				initialMinWoCount: qp.minWoCount ? parseSafeInt(qp.minWoCount, 5) : 5,
				initialChartComparisons: hasParams
					? queryParamsToChartComparisons(qp)
					: getDefaultChartComparisons(),
			};
		}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const [loading, setLoading] = useState(true);
	const [chartLoading, setChartLoading] = useState(true);
	const [prioritiesLoading, setPrioritiesLoading] = useState(true);
	const [error, setError] = useState('');
	const [prioritySummary, setPrioritySummary] = useState([]);
	const [supplierPerformance, setSupplierPerformance] = useState([]);
	const [allMonthlyMetricData, setAllMonthlyMetricData] = useState<Record<string, any[]>>({});
	const [monthlySpend, setMonthlySpend] = useState([]);
	const [chartComparisons, setChartComparisons] =
		useState<Record<string, ComparisonState>>(initialChartComparisons);
	const [comparisonData, setComparisonData] = useState<Record<string, any[]>>({});
	const [comparisonLoading, setComparisonLoading] = useState<Record<string, boolean>>({});

	const [activeFilters, setActiveFilters] = useState(initialFilters);
	const [draftFilters, setDraftFilters] = useState(initialFilters);
	const [filterModalVisible, setFilterModalVisible] = useState(false);
	const [selectedPriorityId, setSelectedPriorityId] = useState(initialPriorityId);
	const [minWoCount, setMinWoCount] = useState(initialMinWoCount);
	const [viewMode, setViewMode] = useState(VIEW_MODE.ALL);
	const [createdAtPickerDates, setCreatedAtPickerDates] = useState(null);
	const [completedAtPickerDates, setCompletedAtPickerDates] = useState(null);

	useEffect(() => {
		const filterParams = filtersToQueryParams(activeFilters, selectedPriorityId, minWoCount);
		const comparisonParams = chartComparisonsToQueryParams(chartComparisons);
		const search = queryString.stringify({ ...filterParams, ...comparisonParams });
		history.replace(location.pathname + (search ? '?' + search : ''));
	}, [activeFilters, selectedPriorityId, minWoCount, chartComparisons]); // eslint-disable-line react-hooks/exhaustive-deps

	const overviewPath = useMemo(() => `/${userType}/analytics/lifecycle/overview`, [userType]);

	const navbarBackgroundColor = useMemo(() => {
		const navColor = nullSafeGetOrElse('detail.config.theme.navblock', companyConfig, '#6237a0');
		return Color(navColor).darken(0.2).toString();
	}, [companyConfig]);

	useEffect(() => {
		if (!updateNavbar || !clearNavbar) return;
		if (viewMode === VIEW_MODE.ALL) {
			clearNavbar();
			return;
		}
		updateNavbar(BACK_LINK_TEXT, overviewPath, navbarBackgroundColor);
	}, [viewMode, overviewPath, navbarBackgroundColor, updateNavbar, clearNavbar]);

	useMountEffect(() => {
		return () => {
			if (clearNavbar) clearNavbar();
		};
	});

	useEffect(() => {
		const search = location.search;
		const str = search && search[0] === '?' ? search.slice(1) : search;
		const qp = queryString.parse(str || '');
		const urlPriorityId = qp.priorityId ? parseSafeInt(qp.priorityId, null) : null;
		if (urlPriorityId === null && selectedPriorityId !== null) {
			setSelectedPriorityId(null);
			setViewMode(VIEW_MODE.ALL);
		}
	}, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

	const isAutoApprovalEnabled = useMemo(() => {
		const approvalConfig = nullSafeGetOrElse('detail.config.approvalConfig', companyConfig, {});
		return (
			nullSafeGetOrElse('autoApproveServiceRequests', approvalConfig, false) &&
			nullSafeGetOrElse('autoApproveSupplierInitiatedWorkOrders', approvalConfig, false)
		);
	}, [companyConfig]);

	const loadChartData = useCallback(
		(filters, priorityId) => {
			setChartLoading(true);
			const params = filtersToParams(filters);
			if (priorityId !== null) {
				params.woPriorityId = priorityId;
			}
			const chartRequests = [
				fetchSupplierPerformance({}, params),
				...(isAutoApprovalEnabled ? [] : [fetchMonthlyApprovalTime({}, params)]),
				fetchMonthlyResponseTime({}, params),
				fetchMonthlyResolutionTime({}, params),
				fetchMonthlyFirstTimeFixRate({}, params),
				fetchMonthlySpend({}, params),
			];
			Promise.all(chartRequests)
				.then((result) => {
					const [supplierData, ...metricResults] = result;
					const [approvalData, responseData, resolutionData, ftfrData, spendData] =
						isAutoApprovalEnabled ? [null, ...metricResults] : metricResults;
					setSupplierPerformance(supplierData || []);
					setAllMonthlyMetricData({
						...(isAutoApprovalEnabled ? {} : { [CHART_METRIC.APPROVAL_TIME]: approvalData || [] }),
						[CHART_METRIC.RESPONSE_TIME]: responseData || [],
						[CHART_METRIC.RESOLUTION_TIME]: resolutionData || [],
						[CHART_METRIC.FIRST_TIME_FIX_RATE]: ftfrData || [],
					});
					setMonthlySpend(spendData || []);
				})
				.catch((e) => {
					setError((e && e.message) || String(e) || 'Failed to load analytics data.');
				})
				.finally(() => {
					setChartLoading(false);
				});
		},
		[
			fetchSupplierPerformance,
			fetchMonthlyApprovalTime,
			fetchMonthlyResponseTime,
			fetchMonthlyResolutionTime,
			fetchMonthlyFirstTimeFixRate,
			fetchMonthlySpend,
			isAutoApprovalEnabled,
		]
	);

	const loadAllData = useCallback(
		(filters, priorityIdHint) => {
			setLoading(true);
			setChartLoading(true);
			setError('');
			const params = filtersToParams(filters);
			fetchPrioritySummary({}, params)
				.then((priorityData) => {
					const data = priorityData || [];
					setPrioritySummary(data);
					const sorted = data.slice().sort((a, b) => a.woPriorityId - b.woPriorityId);
					const hintIsValid =
						priorityIdHint !== null &&
						priorityIdHint !== undefined &&
						sorted.some((row) => row.woPriorityId === priorityIdHint);

					setViewMode(VIEW_MODE.ALL);
					setSelectedPriorityId(null);
					setSupplierPerformance([]);
					setAllMonthlyMetricData({});
					setMonthlySpend([]);

					if (!hintIsValid) {
						return null;
					}
					setSelectedPriorityId(priorityIdHint);
					setViewMode(VIEW_MODE.SINGLE);
					const chartParams = filtersToParams(filters);
					chartParams.woPriorityId = priorityIdHint;
					return Promise.all([
						fetchSupplierPerformance({}, chartParams),
						...(isAutoApprovalEnabled ? [] : [fetchMonthlyApprovalTime({}, chartParams)]),
						fetchMonthlyResponseTime({}, chartParams),
						fetchMonthlyResolutionTime({}, chartParams),
						fetchMonthlyFirstTimeFixRate({}, chartParams),
						fetchMonthlySpend({}, chartParams).catch(() => []),
					]);
				})
				.then((chartResult) => {
					if (!chartResult) {
						return;
					}
					const [supplierData, ...metricResults] = chartResult;
					const [approvalData, responseData, resolutionData, ftfrData, monthlySpendData] =
						isAutoApprovalEnabled ? [null, ...metricResults] : metricResults;
					setSupplierPerformance(supplierData || []);
					setAllMonthlyMetricData({
						...(isAutoApprovalEnabled ? {} : { [CHART_METRIC.APPROVAL_TIME]: approvalData || [] }),
						[CHART_METRIC.RESPONSE_TIME]: responseData || [],
						[CHART_METRIC.RESOLUTION_TIME]: resolutionData || [],
						[CHART_METRIC.FIRST_TIME_FIX_RATE]: ftfrData || [],
					});
					setMonthlySpend(monthlySpendData || []);
				})
				.catch((e) => {
					setError((e && e.message) || String(e) || 'Failed to load Lifecycle Analytics data.');
				})
				.finally(() => {
					setLoading(false);
					setChartLoading(false);
				});
		},
		[
			fetchPrioritySummary,
			fetchSupplierPerformance,
			fetchMonthlyApprovalTime,
			fetchMonthlyResponseTime,
			fetchMonthlyResolutionTime,
			fetchMonthlyFirstTimeFixRate,
			fetchMonthlySpend,
			isAutoApprovalEnabled,
		]
	);

	const fetchByChartKey: Record<string, any> = useMemo(
		() => ({
			// Approval-time chart is hidden when auto-approval is enabled; skip its
			// fetcher in that case so a URL with an approval-time comparison can't
			// trigger requests (and backend errors) for a chart the user can't see.
			...(isAutoApprovalEnabled
				? {}
				: { [COMPARISON_CHART_KEYS.APPROVAL_TIME]: fetchMonthlyApprovalTime }),
			[COMPARISON_CHART_KEYS.RESPONSE_TIME]: fetchMonthlyResponseTime,
			[COMPARISON_CHART_KEYS.RESOLUTION_TIME]: fetchMonthlyResolutionTime,
			[COMPARISON_CHART_KEYS.FIRST_TIME_FIX_RATE]: fetchMonthlyFirstTimeFixRate,
			[COMPARISON_CHART_KEYS.SPEND]: fetchMonthlySpend,
			[COMPARISON_CHART_KEYS.WO_PERFORMANCE]: fetchSupplierPerformance,
		}),
		[
			isAutoApprovalEnabled,
			fetchMonthlyApprovalTime,
			fetchMonthlyResponseTime,
			fetchMonthlyResolutionTime,
			fetchMonthlyFirstTimeFixRate,
			fetchMonthlySpend,
			fetchSupplierPerformance,
		]
	);

	// Per-chart monotonic request token. Every fetch increments and captures
	// its own id; resolutions are dropped if the latest id has moved on, so
	// older in-flight responses can't overwrite newer state.
	const comparisonRequestIdRef = useRef<Record<string, number>>({});
	const nextComparisonRequestId = useCallback((chartKey: string) => {
		const nextId = (comparisonRequestIdRef.current[chartKey] || 0) + 1;
		comparisonRequestIdRef.current[chartKey] = nextId;
		return nextId;
	}, []);
	const isLatestComparisonRequest = useCallback((chartKey: string, requestId: number) => {
		return comparisonRequestIdRef.current[chartKey] === requestId;
	}, []);

	const loadSingleComparison = useCallback(
		(chartKey: string, state: ComparisonState, filters: any, priorityId: any) => {
			const fn = fetchByChartKey[chartKey];
			if (!fn) return;
			// Bump the token even on early-exit paths so any in-flight stale request
			// for this chart is invalidated.
			const requestId = nextComparisonRequestId(chartKey);
			if (!filters || !filters.completedAt || !filters.completedAt[0] || !filters.completedAt[1]) {
				setComparisonData((prev) => ({ ...prev, [chartKey]: [] }));
				return;
			}
			const range = resolveComparisonRange(filters.completedAt[0], filters.completedAt[1], state);
			if (!range) {
				setComparisonData((prev) => ({ ...prev, [chartKey]: [] }));
				return;
			}
			const comparisonFilters = filtersWithCompletedAtRange(filters, range);
			const params = filtersToParams(comparisonFilters);
			if (priorityId !== null && priorityId !== undefined) {
				params.woPriorityId = priorityId;
			}
			setComparisonLoading((prev) => ({ ...prev, [chartKey]: true }));
			fn({}, params)
				.then((data: any[]) => {
					if (!isLatestComparisonRequest(chartKey, requestId)) return;
					setComparisonData((prev) => ({ ...prev, [chartKey]: data || [] }));
				})
				.catch(() => {
					if (!isLatestComparisonRequest(chartKey, requestId)) return;
					setComparisonData((prev) => ({ ...prev, [chartKey]: [] }));
				})
				.finally(() => {
					if (!isLatestComparisonRequest(chartKey, requestId)) return;
					setComparisonLoading((prev) => ({ ...prev, [chartKey]: false }));
				});
		},
		[fetchByChartKey, nextComparisonRequestId, isLatestComparisonRequest]
	);

	// Refs so the filter-change effect can read latest values without them being deps that retrigger it
	const chartComparisonsRef = useRef(chartComparisons);
	useEffect(() => {
		chartComparisonsRef.current = chartComparisons;
	}, [chartComparisons]);

	const fetchByChartKeyRef = useRef(fetchByChartKey);
	useEffect(() => {
		fetchByChartKeyRef.current = fetchByChartKey;
	}, [fetchByChartKey]);

	// When filters or selected priority change, re-fetch all active comparisons in parallel
	useEffect(() => {
		const activeKeys = ALL_COMPARISON_CHART_KEYS.filter((chartKey) => {
			const state = chartComparisonsRef.current[chartKey];
			return state && state.mode !== COMPARISON_MODE.NONE;
		});

		if (activeKeys.length === 0) return;

		// Build all fetch promises first so every network request fires in the same synchronous turn
		const fetches = activeKeys.map((chartKey) => {
			const state = chartComparisonsRef.current[chartKey];
			const fn = fetchByChartKeyRef.current[chartKey];
			const requestId = nextComparisonRequestId(chartKey);
			if (
				!fn ||
				!activeFilters ||
				!activeFilters.completedAt ||
				!activeFilters.completedAt[0] ||
				!activeFilters.completedAt[1]
			) {
				return { chartKey, requestId, promise: Promise.resolve([] as any[]) };
			}
			const range = resolveComparisonRange(
				activeFilters.completedAt[0],
				activeFilters.completedAt[1],
				state
			);
			if (!range) {
				return { chartKey, requestId, promise: Promise.resolve([] as any[]) };
			}
			const comparisonFilters = filtersWithCompletedAtRange(activeFilters, range);
			const params = filtersToParams(comparisonFilters);
			if (selectedPriorityId !== null && selectedPriorityId !== undefined) {
				params.woPriorityId = selectedPriorityId;
			}
			return { chartKey, requestId, promise: fn({}, params).catch(() => [] as any[]) };
		});

		setComparisonLoading((prev) => {
			const next = { ...prev };
			fetches.forEach(({ chartKey }) => {
				next[chartKey] = true;
			});
			return next;
		});

		fetches.forEach(({ chartKey, requestId, promise }) => {
			promise
				.then((data: any[]) => {
					if (!isLatestComparisonRequest(chartKey, requestId)) return;
					setComparisonData((prev) => ({ ...prev, [chartKey]: data || [] }));
				})
				.finally(() => {
					if (!isLatestComparisonRequest(chartKey, requestId)) return;
					setComparisonLoading((prev) => ({ ...prev, [chartKey]: false }));
				});
		});
	}, [activeFilters, selectedPriorityId, nextComparisonRequestId, isLatestComparisonRequest]); // eslint-disable-line react-hooks/exhaustive-deps

	// Single-chart dropdown change: only fetch that one chart, not all 5
	const setComparisonForChart = useCallback(
		(chartKey: string, nextState: ComparisonState) => {
			setChartComparisons((prev) => ({ ...prev, [chartKey]: nextState }));
			if (!nextState || nextState.mode === COMPARISON_MODE.NONE) {
				// Invalidate any in-flight request so it can't repopulate data after reset.
				nextComparisonRequestId(chartKey);
				setComparisonData((prev) => {
					if (!(chartKey in prev)) return prev;
					const next = { ...prev };
					delete next[chartKey];
					return next;
				});
				setComparisonLoading((prev) => {
					if (!(chartKey in prev)) return prev;
					const next = { ...prev };
					delete next[chartKey];
					return next;
				});
				return;
			}
			loadSingleComparison(chartKey, nextState, activeFilters, selectedPriorityId);
		},
		[activeFilters, selectedPriorityId, loadSingleComparison, nextComparisonRequestId]
	);

	useEffect(() => {
		Promise.resolve(fetchWorkOrderPriorities())
			.catch((e) => {
				console.error('Failed to load work order priorities', e);
			})
			.finally(() => {
				setPrioritiesLoading(false);
			});
		loadAllData(initialFilters, initialPriorityId);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// When the completed-at range filter is cleared, comparison ranges can no
	// longer be derived. Reset every chart's comparison back to NONE and clear
	// any cached comparison data so the UI stops referencing stale state.
	const hasCompletedAtRange = !!(
		activeFilters &&
		activeFilters.completedAt &&
		activeFilters.completedAt[0] &&
		activeFilters.completedAt[1]
	);
	useEffect(() => {
		if (hasCompletedAtRange) return;
		setChartComparisons((prev) => {
			let changed = false;
			const next: Record<string, ComparisonState> = { ...prev };
			ALL_COMPARISON_CHART_KEYS.forEach((chartKey) => {
				const current = prev[chartKey];
				if (current && current.mode !== COMPARISON_MODE.NONE) {
					next[chartKey] = { mode: COMPARISON_MODE.NONE };
					changed = true;
				}
			});
			return changed ? next : prev;
		});
		// Bump every chart's request id so any in-flight comparison fetch kicked off
		// before this clear is invalidated. Without this, a late response could
		// repopulate comparisonData after we just emptied it.
		ALL_COMPARISON_CHART_KEYS.forEach((chartKey) => {
			nextComparisonRequestId(chartKey);
		});
		setComparisonData({});
		setComparisonLoading({});
	}, [hasCompletedAtRange, nextComparisonRequestId]);

	const priorityMap = useMemo(() => {
		return nullSafeGetOrElse('records', workOrderPriorities, {});
	}, [workOrderPriorities]);

	const openFilterModal = () => {
		setDraftFilters({ ...activeFilters });
		setFilterModalVisible(true);
	};

	const applyFilters = () => {
		setActiveFilters(draftFilters);
		setFilterModalVisible(false);
		loadAllData(draftFilters, selectedPriorityId);
	};

	const cancelFilters = () => {
		setFilterModalVisible(false);
	};

	const removeProblemTypeFilter = () => {
		const newFilters = { ...activeFilters, problemTypeIds: [] };
		setActiveFilters(newFilters);
		loadAllData(newFilters, selectedPriorityId);
	};

	const removeIsPMFilter = () => {
		const newFilters = { ...activeFilters, isPM: undefined };
		setActiveFilters(newFilters);
		loadAllData(newFilters, selectedPriorityId);
	};

	const removeDateFilter = useCallback(
		(field) => {
			const newFilters = { ...activeFilters, [field]: null };
			setActiveFilters(newFilters);
			loadAllData(newFilters, selectedPriorityId);
		},
		[activeFilters, selectedPriorityId, loadAllData]
	);

	const handleArrowClick = useCallback(
		(priorityId) => {
			if (viewMode === VIEW_MODE.ALL) {
				setSelectedPriorityId(priorityId);
				setViewMode(VIEW_MODE.SINGLE);
				loadChartData(activeFilters, priorityId);
				return;
			}

			if (selectedPriorityId === priorityId) return;
			setSelectedPriorityId(priorityId);
			setViewMode(VIEW_MODE.SINGLE);
			loadChartData(activeFilters, priorityId);
		},
		[viewMode, selectedPriorityId, activeFilters, loadChartData]
	);

	const handleCardClick = useCallback(
		(priorityId) => {
			if (selectedPriorityId === priorityId) return;
			setSelectedPriorityId(priorityId);
			setViewMode(VIEW_MODE.SINGLE);
			loadChartData(activeFilters, priorityId);
		},
		[selectedPriorityId, activeFilters, loadChartData]
	);

	const navigateToSupplierDetail = useCallback(
		(supplierFacilityId) => {
			if (!supplierFacilityId) return;
			const filterParams = filtersToQueryParams(
				activeFilters,
				selectedPriorityId,
				minWoCount,
				viewMode
			);
			const pathName = '/' + userType + '/analytics/lifecycle/overview';
			const backlinkParts = {
				pathName: pathName,
				backLinkText: 'Back to Lifecycle Analytics',
				...filterParams,
			};
			const backlinkParams = encodeURIComponent(queryString.stringify(backlinkParts));
			history.push(
				'/' +
					userType +
					'/analytics/kpi/vendors/detail/' +
					supplierFacilityId +
					'?backlinkParams=' +
					backlinkParams
			);
		},
		[activeFilters, selectedPriorityId, minWoCount, viewMode, userType, history]
	);

	const handleRemoveCreatedAtFilter = useCallback(
		() => removeDateFilter('createdAt'),
		[removeDateFilter]
	);
	const handleRemoveCompletedAtFilter = useCallback(
		() => removeDateFilter('completedAt'),
		[removeDateFilter]
	);

	const handleProblemTypeChange = useCallback(
		(ids) => setDraftFilters((prev) => ({ ...prev, problemTypeIds: ids || [] })),
		[]
	);
	const handleCreatedAtCalendarChange = useCallback((dates) => setCreatedAtPickerDates(dates), []);
	const handleCreatedAtChange = useCallback((dates) => {
		setDraftFilters((prev) => ({ ...prev, createdAt: clampDateRange(dates) }));
		setCreatedAtPickerDates(null);
	}, []);
	const handleCreatedAtOpenChange = useCallback((open) => {
		if (!open) setCreatedAtPickerDates(null);
	}, []);

	const handleCompletedAtCalendarChange = useCallback(
		(dates) => setCompletedAtPickerDates(dates),
		[]
	);
	const handleCompletedAtChange = useCallback((dates) => {
		setDraftFilters((prev) => ({ ...prev, completedAt: clampDateRange(dates) }));
		setCompletedAtPickerDates(null);
	}, []);
	const handleCompletedAtOpenChange = useCallback((open) => {
		if (!open) setCompletedAtPickerDates(null);
	}, []);

	const handleIsPMChange = useCallback(
		(value) => setDraftFilters((prev) => ({ ...prev, isPM: value })),
		[]
	);
	return {
		companyConfig,
		currentUser,
		userType,
		problemTypes,
		fetchProblemTypes,
		history,
		loading,
		chartLoading,
		prioritiesLoading,
		error,
		prioritySummary,
		supplierPerformance,
		allMonthlyMetricData,
		monthlySpend,
		chartComparisons,
		comparisonData,
		comparisonLoading,
		setComparisonForChart,
		activeFilters,
		draftFilters,
		filterModalVisible,
		selectedPriorityId,
		minWoCount,
		setMinWoCount,
		viewMode,
		createdAtPickerDates,
		completedAtPickerDates,
		isAutoApprovalEnabled,
		priorityMap,
		handleArrowClick,
		handleCardClick,
		navigateToSupplierDetail,
		openFilterModal,
		applyFilters,
		cancelFilters,
		removeProblemTypeFilter,
		removeIsPMFilter,
		handleRemoveCreatedAtFilter,
		handleRemoveCompletedAtFilter,
		handleProblemTypeChange,
		handleCreatedAtCalendarChange,
		handleCreatedAtChange,
		handleCreatedAtOpenChange,
		handleCompletedAtCalendarChange,
		handleCompletedAtChange,
		handleCompletedAtOpenChange,
		handleIsPMChange,
	};
}
